import { supabase } from '../lib/supabase';

class AmazonProductsService {
  constructor() {
    this.baseURL = this.resolveBaseUrl();
  }

  resolveBaseUrl() {
    const envUrl = import.meta.env?.VITE_API_BASE_URL;
    const isLocal = typeof envUrl === 'string' && /localhost|127\.0\.0\.1/i.test(envUrl);
    if (typeof window !== 'undefined') {
      if (!isLocal && envUrl) return envUrl;
      return window.location.origin;
    }
    return (!isLocal && envUrl) ? envUrl : 'https://mercadobulk.com';
  }

  // Test backend connection
  async testConnection() {
    try {
      const response = await fetch(`${this.baseURL}/api/test-connection`);
      const result = await response?.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: 'Failed to connect to backend server',
        error: error?.message
      };
    }
  }

  // Get products from backend API
  async getProducts(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        limit: params?.limit || 100,
        offset: params?.offset || 0,
        search: params?.search || '',
        category: params?.category || ''
      });

      const response = await fetch(`${this.baseURL}/api/products?${queryParams}`);
      const result = await response?.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: 'Failed to fetch products from backend',
        error: error?.message,
        data: []
      };
    }
  }

  // Download products from Amazon using backend
  async downloadFromAmazon(params = {}) {
    try {
      const { keyword, category = 'All', maxResults = 50 } = params;

      if (!keyword) {
        throw new Error('Keyword is required for Amazon product search');
      }

      const response = await fetch(`${this.baseURL}/api/amazon/download-products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          keyword,
          category,
          maxResults
        })
      });

      const result = await response?.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: 'Failed to download products from Amazon',
        error: error?.message,
        data: []
      };
    }
  }

  // Supabase operations for products
  async getProductsFromSupabase(params = {}) {
    try {
      const { limit = 100, offset = 0, search = '', category = '' } = params;
      
      let query = supabase
        ?.from('amazon_products')
        ?.select(`
          *,
          user_profiles(email, full_name)
        `)
        ?.order('created_at', { ascending: false })
        ?.range(offset, offset + limit - 1);

      if (search) {
        query = query?.or(`titulo.ilike.%${search}%,descripcion.ilike.%${search}%,categoria.ilike.%${search}%`);
      }

      if (category) {
        query = query?.ilike('categoria', `%${category}%`);
      }

      const { data, error, count } = await query;

      if (error) {
        throw error;
      }

      // Transform to match new 27-column Apify format
  const transformedData = data?.map(row => ({
        'asin': row?.amazon_asin || row?.asin || '',
        'originalAsin': row?.amazon_parent_asin || row?.originalAsin || '',
        'título': row?.titulo || row?.title || '',
        'precio': row?.precio || row?.price || '',
        'marca': row?.marca || row?.brand || '',
        'descripción': row?.descripcion || row?.description || '',
        'Descripción general del producto': row?.overview || '',
        'características': row?.features || '',
        'atributos': row?.atributos || row?.attributes || '',
        'atributos de variante': row?.variant_attributes || '',
        'Detalles de la variante': row?.variant_details || '',
        'variantesAsins': row?.variant_asins || '',
  'Imágenes de alta resolución': Array.isArray(row?.images) ? row.images.join(', ') : (row?.imagenes_alta || ''),
        'URL de producto no normalizada': row?.unNormalizedProductUrl || '',
        'URL': row?.url || '',
        'texto de ubicación': row?.texto_ubicacion || '',
        'Reseñas de la página del producto': row?.product_page_reviews || '',
        'Información importante': row?.informacion_importante || ''
      })) || [];

      return {
        success: true,
        data: transformedData,
        total: count || data?.length || 0,
        message: `Retrieved ${transformedData?.length} products from Supabase`
      };

    } catch (error) {
      return {
        success: false,
        message: 'Failed to fetch products from Supabase',
        error: error?.message,
        data: []
      };
    }
  }

  // Save Amazon sync log
  async saveSyncLog(logData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        ?.from('amazon_sync_logs')
        ?.insert({
          user_id: user?.id,
          sync_type: logData?.sync_type || 'manual',
          keyword: logData?.keyword,
          category: logData?.category,
          products_fetched: logData?.products_fetched || 0,
          products_processed: logData?.products_processed || 0,
          status: logData?.status || 'completed',
          error_message: logData?.error_message,
          sync_data: logData?.sync_data || {},
          completed_at: new Date()?.toISOString()
        });

      return { data, error };
    } catch (error) {
      return { 
        data: null, 
        error: { message: error?.message } 
      };
    }
  }

  // Get sync history
  async getSyncHistory(limit = 10) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        ?.from('amazon_sync_logs')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      return { data, error };
    } catch (error) {
      return { 
        data: null, 
        error: { message: error?.message } 
      };
    }
  }

  // Get connection status
  getConnectionStatus() {
    return {
      backendConnected: true,
      supabaseConnected: !!supabase,
      apiBaseUrl: this.baseURL
    };
  }

  // Optimized bulkCreateProducts method
  async bulkCreateProducts(products, options = {}) {
    const {
      batchSize = 10,
      delayBetweenBatches = 2000,
      onProgress = () => {},
      onError = () => {}
    } = options;

    const results = {
      successful: [],
      failed: [],
      total: products?.length,
      completed: 0
    };

    for (let i = 0; i < products?.length; i += batchSize) {
      const batch = products?.slice(i, i + batchSize);

      try {
        const batchResults = await Promise.allSettled(batch.map(product => this.createProduct(product)));

        batchResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            results.successful.push({
              product: batch[index],
              result: result.value
            });
          } else {
            results.failed.push({
              product: batch[index],
              error: result.reason
            });
            onError(result.reason, batch[index]);
          }
        });

        results.completed += batch.length;
        onProgress({
          completed: results.completed,
          total: results.total,
          successful: results.successful.length,
          failed: results.failed.length,
          percentage: Math.round((results.completed / results.total) * 100)
        });

        if (i + batchSize < products.length) {
          await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
        }
      } catch (error) {
        console.error('Batch processing error:', error);
      }
    }

    return results;
  }
}

export default new AmazonProductsService();